// Google Analytics 4 utility functions for Lite Pocket Deals
import { getDealPricing } from './dealPricing';

// Check if gtag is available
const isGtagAvailable = () => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

// Track product page view with deal metadata
export const trackProductView = (dealData) => {
  if (!isGtagAvailable() || !dealData) return;

  const { _id, title } = dealData;
  const { salePrice, originalPrice, percentOff, promoCode } = getDealPricing(dealData);

  window.gtag('event', 'product_page_view', {
    custom_parameter_1: 'product_page',
    deal_id: _id,
    deal_title: title?.substring(0, 100) || 'Unknown Deal', // Limit title length
    discount_percent: percentOff,
    has_promo_code: !!promoCode,
    price_range: getPriceRange(originalPrice),
    deal_value: salePrice || 0,
  });

  // Enhanced E-commerce tracking
  window.gtag('event', 'view_item', {
    currency: 'USD',
    value: salePrice || 0,
    items: [{
      item_id: _id,
      item_name: title?.substring(0, 100) || 'Unknown Deal',
      item_category: 'Deal',
      discount: percentOff,
      price: salePrice || 0,
      quantity: 1,
    }],
  });
};

// Track when user clicks "Get Deal on Amazon" button
// Uses transport_type: 'beacon' to ensure delivery even during page navigation
export const trackShopButtonClick = (dealData, timeSpentOnPage = 0, trafficSource = 'direct') => {
  if (!isGtagAvailable() || !dealData) return;

  const { _id, title } = dealData;
  const { salePrice, percentOff, promoCode } = getDealPricing(dealData);

  // Use beacon transport to guarantee delivery during navigation
  window.gtag('event', 'shop_button_click', {
    custom_parameter_1: 'conversion',
    deal_id: _id,
    deal_title: title?.substring(0, 100) || 'Unknown Deal',
    conversion_type: 'primary_cta',
    time_to_convert: Math.round(timeSpentOnPage / 1000), // Convert to seconds
    has_promo_code: !!promoCode,
    traffic_source: trafficSource,
    transport_type: 'beacon',
  });

  // Enhanced E-commerce conversion tracking
  window.gtag('event', 'select_item', {
    currency: 'USD',
    value: salePrice || 0,
    items: [{
      item_id: _id,
      item_name: title?.substring(0, 100) || 'Unknown Deal',
      item_category: 'Deal',
      discount: percentOff,
      price: salePrice || 0,
      quantity: 1,
    }],
    transport_type: 'beacon',
  });

  // Track as conversion goal
  window.gtag('event', 'conversion', {
    send_to: 'G-QY3LD2D1ZP/shop_click',
    transport_type: 'beacon',
  });
};

// Track search queries
export const trackSearch = (searchTerm, resultsCount = 0, searchType = 'keyword') => {
  if (!isGtagAvailable() || !searchTerm) return;

  // GA4 built-in search event (populates searchTerm dimension)
  window.gtag('event', 'search', {
    search_term: searchTerm.substring(0, 100),
  });

  // Custom event with more details
  window.gtag('event', 'view_search_results', {
    search_term: searchTerm.substring(0, 100),
    results_count: resultsCount,
    search_type: searchType, // 'keyword', 'semantic', 'category'
  });
};

// Track promo code copy events
export const trackPromoCodeCopy = (dealData) => {
  if (!isGtagAvailable() || !dealData) return;

  const { _id, title, promo_code, promoCode } = dealData;

  window.gtag('event', 'promo_code_copy', {
    custom_parameter_1: 'engagement',
    deal_id: _id,
    deal_title: title?.substring(0, 100) || 'Unknown Deal',
    promo_code: promo_code || promoCode || 'unknown',
    engagement_type: 'code_copy',
  });
};

// Track scroll depth milestones
export const trackScrollMilestone = (dealId, scrollDepth) => {
  if (!isGtagAvailable()) return;

  window.gtag('event', 'scroll_milestone', {
    custom_parameter_1: 'engagement',
    deal_id: dealId,
    scroll_depth: scrollDepth,
    engagement_type: 'scroll',
  });
};

// Track time spent milestones
export const trackTimeMilestone = (dealId, timeSpent) => {
  if (!isGtagAvailable()) return;

  window.gtag('event', 'time_milestone', {
    custom_parameter_1: 'engagement',
    deal_id: dealId,
    time_spent: `${timeSpent}_seconds`,
    engagement_type: 'time_spent',
  });
};

// Track social sharing
export const trackSocialShare = (dealData, platform) => {
  if (!isGtagAvailable() || !dealData) return;

  const { _id, title } = dealData;

  window.gtag('event', 'share', {
    method: platform,
    content_type: 'deal',
    item_id: _id,
    deal_title: title?.substring(0, 100) || 'Unknown Deal',
  });
};

// Track related deal clicks
export const trackRelatedDealClick = (originalDealId, clickedDealId, position) => {
  if (!isGtagAvailable()) return;

  window.gtag('event', 'related_deal_click', {
    custom_parameter_1: 'navigation',
    original_deal_id: originalDealId,
    clicked_deal_id: clickedDealId,
    position,
    engagement_type: 'related_click',
  });
};

// Track page exit without conversion (called on beforeunload)
export const trackPageExit = (dealId, timeSpentOnPage, didConvert = false) => {
  if (!isGtagAvailable()) return;

  window.gtag('event', 'page_exit', {
    custom_parameter_1: 'drop_off',
    deal_id: dealId,
    time_spent: Math.round(timeSpentOnPage / 1000),
    converted: didConvert,
    exit_type: didConvert ? 'converted' : 'dropped_off',
  });
};

// Helper function to categorize price ranges
const getPriceRange = (price) => {
  const numPrice = parseFloat(price) || 0;
  if (numPrice === 0) return 'unknown';
  if (numPrice <= 25) return '0-25';
  if (numPrice <= 100) return '26-100';
  if (numPrice <= 500) return '101-500';
  return '500+';
};

// Custom hook for scroll tracking
export const useScrollTracking = (dealId) => {
  if (typeof window === 'undefined') return;

  const scrollMilestones = { 25: false, 50: false, 75: false, 100: false };

  const handleScroll = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrollPercent = Math.round((scrollTop / docHeight) * 100);

    // Track milestones
    Object.keys(scrollMilestones).forEach(milestone => {
      if (scrollPercent >= parseInt(milestone) && !scrollMilestones[milestone]) {
        scrollMilestones[milestone] = true;
        trackScrollMilestone(dealId, `${milestone}%`);
      }
    });
  };

  window.addEventListener('scroll', handleScroll, { passive: true });

  return () => {
    window.removeEventListener('scroll', handleScroll);
  };
};

// Custom hook for time tracking
export const useTimeTracking = (dealId) => {
  if (typeof window === 'undefined') return { getTimeSpent: () => 0, cleanup: () => {} };

  const startTime = Date.now();
  const timeMilestones = { 10: false, 30: false, 60: false, 120: false };

  const checkTimeMilestones = () => {
    const timeSpent = Math.round((Date.now() - startTime) / 1000);

    Object.keys(timeMilestones).forEach(milestone => {
      if (timeSpent >= parseInt(milestone) && !timeMilestones[milestone]) {
        timeMilestones[milestone] = true;
        trackTimeMilestone(dealId, milestone);
      }
    });
  };

  const interval = setInterval(checkTimeMilestones, 5000); // Check every 5 seconds

  const getTimeSpent = () => Date.now() - startTime;

  const cleanup = () => {
    clearInterval(interval);
  };

  return { getTimeSpent, cleanup };
};
